import { ConsumptionCalculationBase } from '../../../../../types/ConumptionCalculationBase';
import { CategoryState, ReachInformationCategory } from '../../../../../types/ReachInformationMessage';
import { Tag } from '../../../../cdl/Tag/Tag';
import { Text } from '../../../../cdl/Text/Text';
import { Tooltip } from '../../../../cdl/Tooltip/Tooltip';
import { formatNumber } from '../../../../common/helpers/formatNumber.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { Box } from '../../../../common/ui/Box';

interface DaysLeftSailingProps {
    reachInformation: ReachInformationCategory;
}

export const DaysLeftSailing = ({ reachInformation }: DaysLeftSailingProps) => {
    const averageConsumption = formatNumber({
        number: reachInformation.averageDailyConsumption,
        maximumFractionDigits: 4,
    });

    const averageConsumptionLabel =
        reachInformation.consumptionCalculationBase === ConsumptionCalculationBase.DYNAMIC
            ? translate('schedule.priceCompare.basedOnCalculatedAverageLubes', { averageConsumption })
            : translate('schedule.priceCompare.basedOnFixedAverageLubes', { averageConsumption });

    const contextLabel = averageConsumptionLabel;

    const daysLeftText =
        reachInformation.daysLeft === 1
            ? translate('schedule.priceCompare.oneDay')
            : translate('schedule.priceCompare.multipleDays', {
                  days: formatNumber({ number: reachInformation.daysLeft, maximumFractionDigits: 0 }),
              });

    if (reachInformation.categoryState === CategoryState.OUT_OF_REACH) {
        return (
            <Tooltip label={contextLabel} side="right" showArrow>
                <span>
                    <Tag variant="negative" emphasis>
                        {translate('schedule.priceCompare.outOfReach')}
                    </Tag>
                </span>
            </Tooltip>
        );
    }

    if (reachInformation.categoryState === CategoryState.SAFETY_RESERVE) {
        const tag = (
            <Tag variant="attention" emphasis>
                {translate('schedule.priceCompare.onSafetyReserve')}
            </Tag>
        );

        if (reachInformation.daysLeft <= 0) {
            return (
                <Tooltip label={contextLabel} side="right" showArrow>
                    <span>{tag}</span>
                </Tooltip>
            );
        }

        const safetyReserveValue = formatNumber({ number: reachInformation.safetyReserve ?? 0 });

        const safetyReserveUnit = translate('schedule.priceCompare.unitLiters');
        const consumptionUnit = translate('schedule.priceCompare.unitLitersPerDay');

        const structuredTooltip = (
            <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                    {translate('schedule.priceCompare.tooltipSafetyReserve')}
                    {Characters.NO_BREAK_SPACE}
                    <Text as="span" weight="semi">
                        {safetyReserveValue} {safetyReserveUnit}
                    </Text>
                </Box>
                <Box>
                    {translate('schedule.priceCompare.tooltipConsumptionRate')}
                    {Characters.NO_BREAK_SPACE}
                    <Text as="span" weight="semi">
                        {averageConsumption} {consumptionUnit}
                    </Text>
                </Box>
                <Box>
                    {translate('schedule.priceCompare.tooltipRemainingSailing')}
                    {Characters.NO_BREAK_SPACE}
                    <Text as="span" weight="semi">
                        {daysLeftText}
                    </Text>
                </Box>
            </Box>
        );

        return (
            <Tooltip label={structuredTooltip} side="right" showArrow>
                <span>{tag}</span>
            </Tooltip>
        );
    }

    if (reachInformation.categoryState === CategoryState.WARNING_LIMIT) {
        const tag = <Tag variant="attention">{translate('schedule.priceCompare.warningLimit')}</Tag>;

        if (reachInformation.daysLeft <= 0) {
            return (
                <Tooltip label={contextLabel} side="right" showArrow>
                    <span>{tag}</span>
                </Tooltip>
            );
        }

        const structuredTooltip = (
            <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                    {translate('schedule.priceCompare.tooltipWarningLimit')}
                    {Characters.NO_BREAK_SPACE}
                    <Text as="span" weight="semi">
                        {formatNumber({ number: reachInformation.tankThreshold ?? 0 })}
                        {Characters.NO_BREAK_SPACE}
                        {translate('schedule.priceCompare.unitLiters')}
                    </Text>
                </Box>
                <Box>
                    {translate('schedule.priceCompare.tooltipConsumptionRate')}
                    {Characters.NO_BREAK_SPACE}
                    <Text as="span" weight="semi">
                        {averageConsumption}
                        {Characters.NO_BREAK_SPACE}
                        {translate('schedule.priceCompare.unitLitersPerDay')}
                    </Text>
                </Box>
                <Box>
                    {translate('schedule.priceCompare.tooltipRemainingSailing')}
                    {Characters.NO_BREAK_SPACE}
                    <Text as="span" weight="semi">
                        {daysLeftText}
                    </Text>
                </Box>
            </Box>
        );

        return (
            <Tooltip label={structuredTooltip} side="right" showArrow>
                <span>{tag}</span>
            </Tooltip>
        );
    }

    return (
        <Tooltip label={contextLabel} side="right" showArrow>
            <Text variant="extraSmall">
                {reachInformation.daysLeft === 1
                    ? translate('schedule.priceCompare.oneDayLeftSailing')
                    : translate('schedule.priceCompare.daysLeftSailing', {
                          daysLeft: formatNumber({
                              number: reachInformation.daysLeft,
                              maximumFractionDigits: 0,
                          }),
                      })}
            </Text>
        </Tooltip>
    );
};
